import { Observable } from 'rxjs/Rx';
import { map, take } from 'rxjs/operators';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';

export function getUniqueFileName(extension: string): string {
  return Date.now() + '.' + extension;
}

/**
 * Returns extension from file name.
 * @param fileName
 */
export function getExtension(fileName: string): string {
  return fileName.split('.').pop().toLowerCase();
}

/**
 * Returns extension from file name.
 * @param fileName
 */
export function getExtensionExcludingQueryParams(fileName: string): string {
  return fileName.split('.').pop().toLowerCase().split('?')[0];
}

/**
 * @param fileName
 */
export function getNameWithoutExtension(fileName: string): string {
  if (getExtension(fileName)) {
    return fileName.split('.').slice(0, -1).join('.');
  }
  return '';
}

export function getFileNameWithExtension(path: string): string {
  const tempArr = path.split('/');
  return tempArr[tempArr.length - 1];
}

/**
 * This will only work for timestamps that are at the end of a file name
 * separated by a dash.
 * `DonorDB-1581717064630.csv` `returns DonorDB.csv`
 * @param fileName
 */
export function removeTimestampFromFileName(fileName: string): string {
  const extension = getExtension(fileName);
  const nameWithStamp = getNameWithoutExtension(fileName);
  const name = nameWithStamp.split('-').slice(0, -1).join('-');
  return name + '.' + extension;
}

export function extensionIsImage(ext: string): boolean {
  return (
    ext === ImageType.PNG || ext === ImageType.JPG || ext === ImageType.JPEG
  );
}

export function validateExtension(
  ext: string,
  acceptables: ImageType[] | FileType[] | (ImageType | FileType)[],
): boolean {
  let valid = false;
  acceptables.forEach((item) => {
    if (item === ext) {
      valid = true;
    }
  });
  return valid;
}

export function dataUrlToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

export function dataURLtoFile(dataurl, filename): File {
  let arr = dataurl.split(','),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

export function fileLessThanMaxMb(file: File, maxMb: number): boolean {
  return bytesToMegaBytes(file.size) < maxMb;
}

export function bytesToMegaBytes(bytes: number): number {
  return bytes / 1024 / 1024;
}

export function getImgSize(file: File): Observable<ImageMeta> {
  let mapLoadedImage = (event): ImageMeta => {
    return {
      width: event.target.width,
      height: event.target.height,
      originalFile: file,
    };
  };
  let img = new Image();
  let $loadedImg = fromEvent(img, 'load').pipe(take(1), map(mapLoadedImage));
  img.src = window.URL.createObjectURL(file);
  return $loadedImg;
}

export function vesselImageNeedsCropping(imageMeta: ImageMeta) {
  if (!isSixteenByNine(imageMeta)) {
    return true;
  }
  return false;
}

export function nameVesselImageFile(vessel: Partial<Vessel>) {
  let boatAddress = vessel.mooringLocation.parkingAddress.split(', ');
  let fileName: string = `Anchor Yacht Rental ${vessel.lengthFt} ${vessel.make} ${vessel.model} in `;
  if (boatAddress[1]) {
    fileName = fileName + `${boatAddress[1]}`;
  }
  if (boatAddress[2]) {
    let boatState = boatAddress[2].split(' ');
    if (boatState[0]) {
      fileName = fileName + ` ${boatState[0]}`;
    }
  }
  fileName = fileName.replace(/[^a-zA-Z0-9 ]/g, '').replace(/ /g, '-');
  return fileName;
}

export function isSixteenByNine(imageMeta: ImageMeta): boolean {
  return imageMeta.width / imageMeta.height === 16 / 9;
}

export function resizeImage(
  src: string,
  newX: number,
  newY: number,
): Promise<string> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      const elem = document.createElement('canvas');
      // Setting resizingFactor to preserve original image's aspect ratio
      let resizingFactor = 1;
      if (img.width > newX && img.width >= img.height) {
        resizingFactor = newX / img.width;
      } else if (img.height > newY && img.height > img.width) {
        resizingFactor = newY / img.height;
      }

      elem.width = img.width > newX ? img.width * resizingFactor : img.width;
      elem.height =
        img.height > newY ? img.height * resizingFactor : img.height;
      const ctx = elem.getContext('2d');
      ctx.drawImage(img, 0, 0, elem.width, elem.height);
      const data = ctx.canvas.toDataURL();
      res(data);
    };
    img.onerror = (error) => rej(error);
  });
}

export interface ImageMeta {
  width: number;
  height: number;
  originalFile: File;
}

// export interface CreateAssociatedURL {
//   createAssociationForOrg(
//     organizationId: number,
//     orgResource: Partial<any>,
//   ): Observable<any>;
// }

export interface AssociatedFilePath {
  url: string;
  organizationId: string;
}

export interface MultiUploadConfig {
  maxFileSizeMb: number;
  maxFileSelection: number;
}

export interface FileSelectionError {
  message: string;
}

export enum MediaType {
  IMAGE = 'image',
  FILE = 'file',
}

export enum FileType {
  PDF = 'pdf',
  CSV = 'csv',
}
export enum ImageType {
  JPG = 'jpg',
  JPEG = 'jpeg',
  PNG = 'png',
  THUMB = 'thumb',
  SVG = 'svg',
}
export enum VideoType {
  MP4 = 'mp4',
}
export enum ResourceType {
  IMAGE = 'image',
  FILE = 'file',
  VIDEO = 'video',
  DONOR_DATABASE = 'donor_database',
  ENRICHED_DONOR_DATABASE = 'enriched_donor_database',
  FAILED_DONOR_DATABASE = 'failed_donor_database',
}
export type ExtensionType = VideoType | ImageType | FileType;

export interface BucketUploadResponse {
  path: string;
  key: string;
  originalFileName: string;
  url: string;
  filepath: string;
}
